import { DocumentRecycleBin } from "./DocumentRecycleBin";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { PanelLeftClose, PanelLeftOpen, Search } from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { BookOpen } from "lucide-react";

import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "../components/ui/empty";
import { FieldError } from "../components/ui/field";
import {
  createOpsDocsGroup,
  createOpsDocsPage,
  createOpsDocsSpace,
  listOpsDocsLinkTargets,
  updateOpsDocsGroup,
} from "./docs-api";
import { DocsGroupsDialog } from "./DocsGroupsDialog";
import { DocsPageEditor } from "./DocsPageEditor";
import { DocsSearch } from "./DocsSearch";
import { DocsReviewsDialog } from "./DocsReviewsDialog";
import { DocsTree } from "./DocsTree";
import { docsBootstrapOptions, useDocsPageQuery } from "./docs-queries";
import { DocsWorkspaceLoading } from "./DocsLoading";
import type { OperationsNavigate } from "./navigation";

function useDocsWorkspaceState(navigate: OperationsNavigate, pageId: string | null) {
  const queryClient = useQueryClient();
  const bootstrap = useQuery(docsBootstrapOptions());
  const pageQuery = useDocsPageQuery(pageId);
  const links = useQuery({
    queryKey: ["ops", "docs", "link-targets"],
    queryFn: listOpsDocsLinkTargets,
    enabled: bootstrap.data?.canWrite === true,
  });
  const refreshBootstrap = () =>
    queryClient.invalidateQueries({ queryKey: ["ops", "docs", "bootstrap"] });
  const createSpace = useMutation({
    mutationFn: createOpsDocsSpace,
    onSuccess: refreshBootstrap,
  });
  const createPage = useMutation({
    mutationFn: createOpsDocsPage,
    onSuccess: async (response) => {
      await refreshBootstrap();
      navigate(`/docs/${response.page.id}`);
    },
  });
  const createGroup = useMutation({
    mutationFn: createOpsDocsGroup,
    onSuccess: refreshBootstrap,
  });
  const updateGroup = useMutation({
    mutationFn: ({
      groupId,
      input,
    }: {
      groupId: string;
      input: Parameters<typeof updateOpsDocsGroup>[1];
    }) => updateOpsDocsGroup(groupId, input),
    onSuccess: refreshBootstrap,
  });
  return { bootstrap, pageQuery, links, createSpace, createPage, createGroup, updateGroup };
}

function DocsHeader({
  createGroup,
  data,
  updateGroup,
  navigate,
  treeCollapsed,
  setTreeCollapsed,
}: {
  treeCollapsed: boolean;
  setTreeCollapsed: (value: boolean) => void;
  navigate: OperationsNavigate;
  createGroup: ReturnType<typeof useDocsWorkspaceState>["createGroup"];
  data: NonNullable<ReturnType<typeof useDocsWorkspaceState>["bootstrap"]["data"]>;
  updateGroup: ReturnType<typeof useDocsWorkspaceState>["updateGroup"];
}) {
  return (
    <header className="flex min-h-12 flex-wrap items-center justify-between gap-3 border-b bg-card px-4 py-1">
      <div className="flex items-center gap-2">
        <Button
          className="hidden lg:inline-flex"
          size="icon-sm"
          variant="ghost"
          aria-label={treeCollapsed ? "Show pages" : "Hide pages"}
          aria-expanded={!treeCollapsed}
          aria-controls="docs-page-tree"
          onClick={() => {
            setTreeCollapsed(!treeCollapsed);
          }}
        >
          {treeCollapsed ? <PanelLeftOpen /> : <PanelLeftClose />}
        </Button>
        <h1 className="text-lg font-semibold text-balance" id="docs-title">
          Docs
        </h1>
      </div>
      <div className="flex items-center gap-2">
        <DocumentRecycleBin />
        <DocsReviewsDialog navigate={navigate} />
        <DocsSearchDialog navigate={navigate} />
        {data.canManage ? (
          <DocsGroupsDialog
            employees={data.employees}
            error={createGroup.error ?? updateGroup.error}
            groups={data.groups}
            isSaving={createGroup.isPending || updateGroup.isPending}
            onCreate={(input) => {
              createGroup.mutate(input);
            }}
            onUpdate={(groupId, input) => {
              updateGroup.mutate({ groupId, input });
            }}
          />
        ) : null}
      </div>
    </header>
  );
}

function DocsWorkspaceReady({
  navigate,
  pageId,
  state,
}: {
  navigate: OperationsNavigate;
  pageId: string | null;
  state: ReturnType<typeof useDocsWorkspaceState>;
}) {
  const [treeCollapsed, setTreeCollapsed] = useState(false);
  const data = state.bootstrap.data;
  if (!data) return null;
  const empty = data.spaces.length === 0 && !data.canManage;
  return (
    <section className="min-w-0" aria-labelledby="docs-title">
      <DocsHeader
        treeCollapsed={treeCollapsed}
        setTreeCollapsed={setTreeCollapsed}
        createGroup={state.createGroup}
        data={data}
        updateGroup={state.updateGroup}
        navigate={navigate}
      />
      {state.links.error ? <FieldError>{state.links.error.message}</FieldError> : null}
      {empty ? (
        <Empty className="min-h-96">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <BookOpen />
            </EmptyMedia>
            <EmptyTitle>No documentation spaces yet</EmptyTitle>
            <EmptyDescription>
              A Docs manager needs to create the first space before pages can be added.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <div
          className={`grid min-w-0 bg-card lg:items-start ${treeCollapsed ? "lg:grid-cols-1" : "lg:grid-cols-[224px_minmax(0,1fr)]"}`}
        >
          <div className={treeCollapsed ? "lg:hidden" : "contents"}>
            <DocsTree
              activePageId={pageId}
              canManage={data.canManage}
              canWrite={data.canWrite}
              navigate={navigate}
              onCreatePage={async (spaceId, parentPageId, title) => {
                await state.createPage.mutateAsync({ spaceId, parentPageId, title });
              }}
              onCreateSpace={async (name) => {
                await state.createSpace.mutateAsync({ name });
              }}
              pageCreateError={state.createPage.error}
              pageCreating={state.createPage.isPending}
              pages={data.pages}
              spaceCreateError={state.createSpace.error}
              spaceCreating={state.createSpace.isPending}
              spaces={data.spaces}
              employees={data.employees}
              groups={data.groups}
            />
          </div>
          <DocsPageEditor
            key={pageId}
            bootstrap={data}
            linkTargets={state.links.data?.targets ?? []}
            navigate={navigate}
            pageId={pageId}
            pageQuery={state.pageQuery}
          />
        </div>
      )}
    </section>
  );
}

export function DocsWorkspace({
  navigate,
  pageId,
}: {
  navigate: OperationsNavigate;
  pageId: string | null;
}) {
  const state = useDocsWorkspaceState(navigate, pageId);

  if (state.bootstrap.isPending) {
    return <DocsWorkspaceLoading />;
  }
  if (state.bootstrap.error)
    return (
      <div className="p-6">
        <FieldError>{state.bootstrap.error.message}</FieldError>
      </div>
    );
  return <DocsWorkspaceReady navigate={navigate} pageId={pageId} state={state} />;
}

function DocsSearchDialog({ navigate }: { navigate: OperationsNavigate }) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger render={<Button variant="ghost" size="sm" />}>
        <Search />
        Search docs
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Find knowledge</DialogTitle>
          <DialogDescription>Search your accessible documents or ask a question.</DialogDescription>
        </DialogHeader>
        <DialogBody>
          <DocsSearch
            navigate={(destination) => {
              navigate(destination);
              setOpen(false);
            }}
          />
        </DialogBody>
        <DialogFooter>
          <DialogClose render={<Button variant="secondary" />}>Done</DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
